import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navItems, siteConfig } from '@/mocks/data';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleNavClick = () => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  };

  const linkClass = (path: string) => {
    const isActive = location.pathname === path || (path === '/' && location.pathname === '/home');
    const base = 'relative whitespace-nowrap transition-all duration-300 cursor-pointer font-medium tracking-wide';

    if (scrolled) {
      const sizeClass = 'px-3 py-1.5 text-[15px]';
      if (isActive) return `${base} ${sizeClass} text-accent-400 after:absolute after:bottom-0 after:left-3 after:right-3 after:h-px after:bg-accent-400`;
      return `${base} ${sizeClass} text-foreground-700 hover:text-accent-500`;
    }

    const sizeClass = 'px-3 py-1.5 text-base';
    if (isActive) return `${base} ${sizeClass} text-background-50 after:absolute after:bottom-0 after:left-3 after:right-3 after:h-px after:bg-background-50/60`;
    return `${base} ${sizeClass} text-background-50/80 hover:text-background-50`;
  };

  const externalLinkClass = () => {
    if (scrolled) {
      return 'ml-2 px-3 py-1 text-[13px] rounded-full border border-background-200/70 text-foreground-600 hover:text-accent-500 hover:border-accent-300/60 transition-colors cursor-pointer whitespace-nowrap font-medium tracking-wide';
    }
    return 'ml-2 px-3 py-1 text-[13px] rounded-full border border-background-50/25 text-background-50/70 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer whitespace-nowrap font-medium tracking-wide';
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-primary-950/25 backdrop-blur-xl h-14'
          : 'bg-transparent h-16 md:h-18'
      }`}
    >
      <div className="w-full px-6 md:px-10 h-full">
        <div className="flex items-center justify-between h-full">
          <Link
            to="/"
            className="flex items-center cursor-pointer"
            aria-label="樂律 Lelyric — 回首頁"
            onClick={handleNavClick}
          >
            <img
              src="https://storage.readdy-site.link/project_files/8e05c265-8616-48c2-b04c-6c0cd591d92e/12c767cc-4ca3-4d8a-9c68-e7b2e2764614_compressed_01logo.webp"
              alt="樂律 Lelyric 品牌視覺"
              className={`object-contain transition-all duration-300 ${
                scrolled ? 'h-8 w-auto' : 'h-9 md:h-10 w-auto'
              }`}
              width="120"
              height="120"
            />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={linkClass(item.path)}
                onClick={handleNavClick}
              >
                <LocalizedNavLabel href={item.path} fallback={item.label} />
              </Link>
            ))}
            <span className="w-px h-4 bg-background-50/15 mx-1.5"></span>
            <a
              href={siteConfig.catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={externalLinkClass()}
            >
              書籍資料庫
              <i className="ri-external-link-line ml-1 text-[11px]"></i>
            </a>
          </div>

          <LanguageSwitcher />

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="選單"
          >
            <i
              className={`text-xl ${scrolled ? 'text-foreground-800' : 'text-background-50'} ${
                mobileOpen ? 'ri-close-line' : 'ri-menu-line'
              }`}
            ></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`px-4 py-2.5 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  (location.pathname === item.path || (item.path === '/' && location.pathname === '/home'))
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <LocalizedNavLabel href={item.path} fallback={item.label} />
              </Link>
            ))}
            <div className="w-full h-px bg-background-200/60 my-1"></div>
            <a
              href={siteConfig.catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleNavClick}
              className="px-4 py-2.5 rounded-md text-base font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
            >
              書籍資料庫
              <i className="ri-external-link-line text-[13px] text-foreground-400"></i>
            </a>
            <a
              href={siteConfig.sonbookUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleNavClick}
              className="px-4 py-2.5 rounded-md text-base font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
            >
              崧博出版主站
              <i className="ri-external-link-line text-[13px] text-foreground-400"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}